import { useCallback, useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canAccessAdmin, canAccessPlatform } from "../utils/permissions";
import { getCanonicalRoles, getCanonicalLabel, roleLabelsFor } from "../lib/canonicalRoles";
import SortDropdown, { sortItems } from "../components/common/SortDropdown";
import {
  Buildings, Users, CreditCard, CheckCircle, Copy, Check,
  UserPlus, Trash, Crown, ShieldCheck, UserCircle, SpinnerGap,
  Envelope, ArrowRight, Warning, PencilSimple, CaretDown, ArrowsClockwise, Globe
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

// Vincent 2026-06-01 — Le "cartable" doit afficher EXCLUSIVEMENT les 8 rôles
// canoniques de "Gestion des rôles" (avec libellés personnalisés par org).
// Plus de liste hardcodée de titres de poste. Cf lib/canonicalRoles.js.

const ROLE_ICONS = {
  founder: Crown,
  president: Crown,
  ceo: Crown,
  vp: ShieldCheck,
  admin: ShieldCheck,
  concepteur: ShieldCheck,
  employee_advanced: UserCircle,
  employee_trained: UserCircle,
  employee_new: UserCircle,
  employee: UserCircle,
  viewer: UserCircle,
};

export default function BillingPage() {
  const { user, checkAuth } = useAuth();
  const { lang, t } = useLang();
  const [searchParams] = useSearchParams();
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [dataRegions, setDataRegions] = useState({});
  const [regionSaving, setRegionSaving] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("employee");
  // Vincent 2026-06-03 (Eric — items 12, 13) — Champs optionnels coupon + nom
  // pour personnaliser le lien d'invitation (pré-remplissage).
  const [inviteName, setInviteName] = useState("");
  const [invitePartnerCode, setInvitePartnerCode] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  // Vincent #217 option 3 (Eric 2026-06-04 fichier 17h) — Création DIRECTE sans
  // invitation. L'admin coche cette case et le compte est créé immédiatement
  // avec un mot de passe temporaire envoyé par email.
  const [createDirectly, setCreateDirectly] = useState(false);
  const [inviteMsg, setInviteMsg] = useState("");
  const [paymentChecked, setPaymentChecked] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [accessibleOrgs, setAccessibleOrgs] = useState([]);
  const [switching, setSwitching] = useState(false);
  // Vincent #219 (Eric 2026-02-06) — Sélecteur de coupon dans le formulaire
  // d'invitation. On charge la liste des coupons existants depuis /api/coupons
  // et on les présente dans un <select> au lieu d'un input texte libre.
  const [availableCoupons, setAvailableCoupons] = useState([]);
  // Vincent #331 (Eric 2026-06-17) — Tri des membres
  const [membersSortBy, setMembersSortBy] = useState("alpha_asc");
  const sortedMembers = useMemo(() => sortItems(members, membersSortBy), [members, membersSortBy]);

  const loadCoupons = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/coupons`, { withCredentials: true });
      setAvailableCoupons(Array.isArray(data) ? data : (data?.coupons || []));
    } catch {
      // Endpoint coupons réservé admin/founder — silencieux si 403.
      setAvailableCoupons([]);
    }
  }, []);

  const loadOrg = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/organizations/me`, { withCredentials: true });
      setOrg(data.organization);
      setMembers(data.members || []);
      setInvites(data.invites || []);
      setPlans(data.plans || {});
      setAccessibleOrgs(data.accessible_orgs || []);
      // Load data regions
      try {
        const regRes = await axios.get(`${API}/api/organizations/data-regions`, { withCredentials: true });
        setDataRegions(regRes.data);
      } catch {}
    } catch {}
    setLoading(false);
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) return;
    try {
      const { data } = await axios.get(`${API}/api/organizations/checkout/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === "paid") {
        setPaymentChecked(true);
        loadOrg();
      } else if (data.status === "expired") {
        setPaymentChecked(true);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  }, [loadOrg]);

  useEffect(() => {
    loadOrg();
    loadCoupons();
    const sessionId = searchParams.get("session_id");
    if (sessionId && !paymentChecked) {
      pollPaymentStatus(sessionId);
    }
  }, [loadOrg, loadCoupons, pollPaymentStatus, searchParams, paymentChecked]);

  const switchOrg = async (orgId) => {
    setSwitching(true);
    try {
      await axios.post(`${API}/api/organizations/switch`, { org_id: orgId }, { withCredentials: true });
      await checkAuth();
      await loadOrg();
    } catch (err) {
      console.error("Switch org failed:", err);
    }
    setSwitching(false);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(org?.partner_code || "");
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    try {
      // Vincent #217 option 3 — branche "Créer directement" vs invitation classique
      if (createDirectly) {
        if (!inviteName.trim()) {
          setInviteMsg("Le nom est requis pour créer un compte directement");
          setInviteLoading(false);
          return;
        }
        const { data } = await axios.post(`${API}/api/organizations/members/direct`, {
          email: inviteEmail,
          name: inviteName,
          role: inviteRole,
          partner_code: invitePartnerCode || undefined,
        }, { withCredentials: true });
        const tempPwd = data.temporary_password;
        const targetEmail = inviteEmail;
        if (data.email_sent) {
          setInviteMsg(`✅ Compte créé pour ${targetEmail}. Email avec mot de passe temporaire envoyé. (Mot de passe : ${tempPwd})`);
        } else {
          setInviteMsg(`⚠️ Compte créé pour ${targetEmail} MAIS l'email n'a PAS été envoyé. Mot de passe temporaire à transmettre manuellement : ${tempPwd}`);
        }
        if (tempPwd && navigator.clipboard) {
          navigator.clipboard.writeText(`Email: ${targetEmail}\nMot de passe temporaire: ${tempPwd}`).catch(() => {});
        }
        setInviteEmail("");
        setInviteName("");
        setInvitePartnerCode("");
        setCreateDirectly(false);
        loadOrg();
        return;
      }
      const { data } = await axios.post(`${API}/api/organizations/invite`, {
        email: inviteEmail,
        role: inviteRole,
        // Vincent 2026-06-03 (Eric — items 12, 13) — Pré-remplissage du lien.
        name: inviteName || undefined,
        partner_code: invitePartnerCode || undefined,
      }, { withCredentials: true });
      // Eric iter84b — affiche TRUTHFULLY le statut email + le register_url
      // pour que l'admin puisse copier-coller le lien manuellement si Resend down.
      const targetEmail = inviteEmail;
      if (data.email_sent) {
        setInviteMsg(`✅ Invitation envoyée à ${targetEmail}. Le destinataire doit vérifier sa boîte (et le dossier SPAM/Junk).`);
      } else {
        const err = data.email_error || "raison inconnue";
        const link = data.register_url || "";
        setInviteMsg(
          `⚠️ Invitation créée pour ${targetEmail} MAIS l'email n'a PAS été envoyé (${err}). ` +
          `Copie le lien suivant et envoie-le manuellement (Outlook, SMS, Slack…) :\n\n${link}`
        );
        if (link && navigator.clipboard) {
          navigator.clipboard.writeText(link).catch(() => {});
        }
      }
      setInviteEmail("");
      setInviteName("");
      setInvitePartnerCode("");
      loadOrg();
    } catch (err) {
      setInviteMsg(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setInviteLoading(false);
    }
  };

  const copyInviteLink = async (inviteId) => {
    try {
      const { data } = await axios.get(`${API}/api/organizations/invites/${inviteId}/link`, { withCredentials: true });
      if (data.register_url) {
        await navigator.clipboard.writeText(data.register_url);
        setInviteMsg(`📋 Lien copié pour ${data.email}. Colle-le dans Outlook/Slack/SMS.`);
        setTimeout(() => setInviteMsg(""), 5000);
      }
    } catch (err) {
      setInviteMsg(err.response?.data?.detail || "Erreur récupération lien");
    }
  };

  const removeMember = async (id) => {
    const ok = await confirmDialog("Retirer ce membre ?", { danger: true, confirmLabel: "Retirer" });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/organizations/members/${id}`, { withCredentials: true });
      loadOrg();
    } catch {}
  };

  const cancelInvite = async (id) => {
    try {
      await axios.delete(`${API}/api/organizations/invites/${id}`, { withCredentials: true });
      loadOrg();
    } catch {}
  };

  const updateMemberRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/api/organizations/members/${memberId}/role`, { role: newRole }, { withCredentials: true });
      setEditingMember(null);
      loadOrg();
      // Vincent 2026-06-03 (Eric) — Confirmation visible. Avant : pas de toast,
      // le changement passait silencieusement et donnait l'impression d'un bug.
      const labelMatch = getCanonicalRoles(user, lang).find(r => r.id === newRole);
      toast.success(`Rôle mis à jour : ${labelMatch ? labelMatch.label : newRole}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du changement de rôle");
    }
  };

  // Vincent #197 (2026-05-27) — Modal dropdown au lieu du prompt() texte libre.
  const [titleEditTarget, setTitleEditTarget] = useState(null); // { memberId, currentTitle }

  const updateMemberTitle = (memberId, currentTitle) => {
    setTitleEditTarget({ memberId, currentTitle: currentTitle || "" });
  };

  const saveMemberTitle = async (memberId, newTitle) => {
    try {
      await axios.put(`${API}/api/organizations/members/${memberId}/title`, { job_title: newTitle.trim() }, { withCredentials: true });
      setTitleEditTarget(null);
      loadOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du changement de titre");
    }
  };

  const startCheckout = async () => {
    try {
      const { data } = await axios.post(`${API}/api/organizations/checkout`, {
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="max-w-xl mx-auto text-center py-16" data-testid="no-org">
        <Buildings size={48} className="mx-auto mb-4 text-zinc-400" />
        <h2 className="text-xl font-bold mb-2">{t("no_organization")}</h2>
        <p className="text-zinc-500 mb-6">{t("no_org_subtitle")}</p>
        <a href="/pricing" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500">
          <ArrowRight size={18} /> {t("choose_plan")}
        </a>
      </div>
    );
  }

  // Vincent iter83 (Eric) — founder OU admin canonique. Source de vérité = 8 rôles
  // canoniques + flag is_founder. Avant : liste legacy bloquait les founders dont
  // org_role n'était pas ceo/president/vp/admin.
  // iter115 (Eric 2026-06-24) — Migration vers helper canonique canAccessAdmin.
  const isAdmin = canAccessAdmin(user);
  const isFounderExempt = org.founder_exempt || org.status === "founder_exempt" || org.status === "active";
  const isPending = !isFounderExempt && org.status !== "active";
  const planInfo = plans[org.plan] || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="billing-page">
      {/* Org Header + Switcher */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold font-['Outfit']" data-testid="org-name">{org.name}</h1>
            {/* Org Switcher for founders */}
            {accessibleOrgs.length > 1 && (
              <div className="relative group">
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 text-cyan-500 text-xs font-medium rounded-lg hover:bg-cyan-500/20 transition-colors"
                  data-testid="org-switcher-btn"
                >
                  <ArrowsClockwise size={14} />
                  Changer
                  <CaretDown size={12} />
                </button>
                <div className="absolute top-full left-0 mt-1 w-64 bg-card border border-border rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50" data-testid="org-switcher-dropdown">
                  <div className="p-2 space-y-1">
                    {accessibleOrgs.map((ao) => (
                      <button
                        key={ao.id}
                        onClick={() => !ao.is_current && switchOrg(ao.id)}
                        disabled={ao.is_current || switching}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                          ao.is_current
                            ? "bg-cyan-500/10 text-cyan-500 font-medium cursor-default"
                            : "hover:bg-secondary text-foreground"
                        }`}
                        data-testid={`switch-org-${ao.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{ao.name}</span>
                          {ao.is_current && <CheckCircle size={14} weight="fill" className="text-cyan-500" />}
                        </div>
                        <span className="text-[10px] text-muted-foreground capitalize">{ao.plan}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <p className="text-sm text-zinc-500">Plan {planInfo.name || org.plan} | {org.billing_cycle === "annual" ? "Annuel" : "Mensuel"}</p>
        </div>
        {org.founder_exempt ? (
          <div className="px-3 py-1.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-500 border border-cyan-500/20 flex items-center gap-1.5" data-testid="org-status">
            <Crown size={12} weight="fill" />
            {t("founder_unlimited")}
          </div>
        ) : (
          <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${org.status === "active" ? "bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400" : "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"}`} data-testid="org-status">
            {org.status === "active" ? t("active") : t("payment_pending")}
          </div>
        )}
      </div>

      {/* Payment CTA — ONLY for non-founder pending orgs.
          Eric 2026-06-25 (iter120 B4) — migré vers canAccessPlatform (équipe
          Knoria exemptée du paiement). */}
      {isPending && !canAccessPlatform(user) && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5 flex items-center justify-between" data-testid="payment-cta">
          <div className="flex items-center gap-3">
            <Warning size={24} className="text-amber-500" />
            <div>
              <h3 className="font-medium text-amber-800 dark:text-amber-200">{t("activate_plan")}</h3>
              <p className="text-sm text-amber-600 dark:text-amber-400">{t("activate_plan_desc")}</p>
            </div>
          </div>
          <button onClick={startCheckout} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 flex items-center gap-2" data-testid="activate-plan-btn">
            <CreditCard size={18} /> Payer maintenant
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground mb-1">{t("members")}</div>
          <div className="text-2xl font-bold">{org.member_count} <span className="text-sm font-normal text-muted-foreground">/ {org.max_users}</span></div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground mb-1">{t("videos")}</div>
          <div className="text-2xl font-bold">{org.video_count} <span className="text-sm font-normal text-muted-foreground">/ {org.max_videos_month === -1 ? "illimite" : org.max_videos_month}</span></div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground mb-1">{t("plan")}</div>
          <div className="text-2xl font-bold">{planInfo.name || org.plan}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground mb-1">{t("partner_code_label")}</div>
          <div className="flex items-center gap-2">
            <code className="text-sm font-mono font-bold text-primary" data-testid="partner-code">{org.partner_code}</code>
            <button onClick={copyCode} className="p-1 hover:bg-secondary rounded" data-testid="copy-code-btn">
              {codeCopied ? <Check size={14} className="text-teal-500" /> : <Copy size={14} className="text-muted-foreground" />}
            </button>
          </div>
        </div>
      </div>

      {/* Data Region Sélection */}
      {isAdmin && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="data-region-section">
          <h3 className="font-semibold mb-1 flex items-center gap-2">
            <Globe size={18} className="text-primary" /> {lang === "fr" ? "Region de stockage des données" : "Data Storage Region"}
          </h3>
          <p className="text-xs text-muted-foreground mb-4">{lang === "fr" ? "Choisissez où les données de votre organisation sont stockées. Requis pour la conformité Loi 25 et PIPEDA." : "Choose where your organization data is stored."}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(dataRegions).map(([key, region]) => {
              const isActive = (org?.data_region || "na-standard") === key;
              return (
                <button
                  key={key}
                  onClick={async () => {
                    if (isActive || regionSaving) return;
                    const ok = await confirmDialog(
                      lang === "fr" ? `Changer la région vers ${region.label} ?` : `Change region to ${region.label_en}?`,
                      { confirmLabel: lang === "fr" ? "Changer" : "Change" },
                    );
                    if (!ok) return;
                    setRegionSaving(true);
                    try {
                      await axios.put(`${API}/api/organizations/data-region`, { data_region: key }, { withCredentials: true });
                      loadOrg();
                    } catch (e) { console.error(e); }
                    setRegionSaving(false);
                  }}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${isActive ? "border-[#002FA7] bg-[#002FA7]/5" : "border-border hover:border-zinc-300"}`}
                  data-testid={`region-${key}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">{lang === "fr" ? region.label : region.label_en}</span>
                    {isActive && <CheckCircle size={16} weight="fill" className="text-[#002FA7]" />}
                  </div>
                  <p className="text-[10px] text-muted-foreground mb-2">{region.location}</p>
                  <div className="flex flex-wrap gap-1">
                    {region.compliance.map(c => (
                      <span key={c} className="text-[9px] px-1.5 py-0.5 bg-teal-500/10 text-teal-600 rounded font-medium">{c}</span>
                    ))}
                  </div>
                  {region.surcharge > 0 && (
                    <p className="text-xs text-amber-600 mt-2 font-medium">+{region.surcharge}$ CAD/mois</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Invite Members */}
      {isAdmin && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="invite-section">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <UserPlus size={18} className="text-primary" /> {t("invite_member")}
          </h3>
          {/* Eric iter83 — Bannière qui rappelle l'org cible. Évite le piège
              multi-org : un founder peut être actif dans Knoria Plateforme tout
              en pensant inviter dans FLHQ. Le switcher d'org en haut du
              dashboard est la seule façon de changer la cible. */}
          <div className="mb-4 rounded-lg border-2 border-blue-200 bg-blue-50 px-4 py-3 text-sm" data-testid="invite-target-org-banner">
            <p className="text-blue-900">
              {lang === "fr" ? "Vous invitez dans : " : "Inviting to: "}
              <strong>{org?.name || "—"}</strong>
            </p>
            <p className="text-xs text-blue-700 mt-1">
              {lang === "fr"
                ? "Pour inviter dans une autre entreprise, changez d'organisation via le sélecteur en haut du dashboard."
                : "To invite into a different org, switch organizations from the top selector."}
            </p>
          </div>
          <form onSubmit={sendInvite} className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">{t("invite_email")}</label>
              <input
                type="email" required value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t("invite_email_placeholder")}
                className="w-full h-11 px-4 border border-border rounded-lg text-sm bg-background focus:ring-2 focus:ring-primary/50 outline-none"
                data-testid="invite-email-input"
              />
            </div>
            {/* Vincent 2026-06-03 (Eric — items 12, 13) — Champs optionnels pour
                personnaliser le lien d'invitation. Le nom pré-remplit le champ
                "Prénom/Nom" à l'inscription; le code partenaire pré-remplit le
                champ "Code partenaire". Tous deux sont OPTIONNELS. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium block mb-1 text-zinc-600">
                  {lang === "fr" ? "Nom (optionnel)" : "Name (optional)"}
                </label>
                <input
                  type="text" value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder={lang === "fr" ? "Pré-rempli à l'inscription" : "Pre-filled on signup"}
                  className="w-full h-10 px-3 border border-border rounded-lg text-sm bg-background focus:ring-2 focus:ring-primary/50 outline-none"
                  data-testid="invite-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1 text-zinc-600">
                  {lang === "fr" ? "Coupon de rabais (optionnel)" : "Discount coupon (optional)"}
                </label>
                {/* Vincent #219 (2026-02-06) — Avant : input texte libre, qui
                    obligeait l'admin à connaître par cœur les codes. Maintenant :
                    <select> avec les coupons disponibles (admin peut en créer
                    depuis /dashboard/admin/coupons). Si le menu déroulant est
                    vide, on garde une saisie libre comme fallback. */}
                {availableCoupons.length > 0 ? (
                  <select
                    value={invitePartnerCode}
                    onChange={(e) => setInvitePartnerCode(e.target.value)}
                    className="w-full h-10 px-3 border border-border rounded-lg text-sm bg-background focus:ring-2 focus:ring-primary/50 outline-none"
                    data-testid="invite-coupon-select"
                  >
                    <option value="">{`— ${lang === "fr" ? "Aucun coupon" : "No coupon"} —`}</option>
                    {availableCoupons.map((c) => (
                      <option key={c.id || c.code} value={c.code}>
                        {`${c.code}${c.discount_percent ? ` (${c.discount_percent}% rabais)` : ""}${c.description ? ` — ${c.description}` : ""}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text" value={invitePartnerCode}
                    onChange={(e) => setInvitePartnerCode(e.target.value.toUpperCase())}
                    placeholder={lang === "fr" ? "Aucun coupon défini — saisir manuellement" : "No coupons defined — enter manually"}
                    className="w-full h-10 px-3 border border-border rounded-lg text-sm bg-background focus:ring-2 focus:ring-primary/50 outline-none uppercase"
                    data-testid="invite-partner-code-input"
                  />
                )}
                {availableCoupons.length === 0 && (
                  <p className="text-[11px] text-zinc-500 mt-1">
                    💡 {lang === "fr"
                      ? <>Crée d'abord tes coupons via <strong>Coupons → Nouveau coupon</strong> pour les voir ici.</>
                      : <>Create coupons first via <strong>Coupons → New coupon</strong> to see them here.</>}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">{t("org_role")}</label>
              <div className="grid grid-cols-2 gap-2" data-testid="invite-role-options">
                {/* Vincent 2026-06-02 — Les 8 rôles canoniques (source unique :
                    Gestion des rôles + roleLabelsFor(user, lang)). Plus de president/ceo/vp
                    séparés. Mapping IDs canoniques → couleur/icône stable. */}
                {(() => {
                  const VISUALS = {
                    viewer:             { icon: UserCircle, bg: "border-zinc-400 bg-zinc-200/40",    text: "text-zinc-700",    iconCls: "text-zinc-600" },
                    employee:           { icon: UserCircle, bg: "border-blue-500 bg-blue-500/10",     text: "text-blue-600",    iconCls: "text-blue-500" },
                    employee_new:       { icon: UserCircle, bg: "border-sky-500 bg-sky-500/10",       text: "text-sky-600",     iconCls: "text-sky-500" },
                    employee_trained:   { icon: UserCircle, bg: "border-teal-500 bg-teal-500/10",     text: "text-teal-600",    iconCls: "text-teal-500" },
                    employee_advanced:  { icon: UserCircle, bg: "border-cyan-500 bg-cyan-500/10",     text: "text-cyan-600",    iconCls: "text-cyan-500" },
                    concepteur:         { icon: ShieldCheck, bg: "border-violet-500 bg-violet-500/10",text: "text-violet-600",  iconCls: "text-violet-500" },
                    admin:              { icon: ShieldCheck, bg: "border-amber-500 bg-amber-500/10",  text: "text-amber-600",   iconCls: "text-amber-500" },
                    founder:            { icon: Crown,       bg: "border-teal-500 bg-teal-500/10", text: "text-teal-600", iconCls: "text-teal-500" },
                  };
                  return getCanonicalRoles(user, lang).map((r) => {
                    const v = VISUALS[r.id] || VISUALS.employee;
                    const Icon = v.icon;
                    const selected = inviteRole === r.id;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setInviteRole(r.id)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          selected ? v.bg : "border-border hover:border-muted-foreground/30"
                        }`}
                        data-testid={`role-option-${r.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon size={16} className={selected ? v.iconCls : "text-muted-foreground"} weight={selected ? "fill" : "regular"} />
                          <span className={`text-sm font-semibold ${selected ? v.text : "text-foreground"}`}>{r.label}</span>
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
            {/* Vincent #217 option 3 (Eric 2026-06-04) — Toggle pour création DIRECTE
                (sans envoi de lien d'invitation). Le compte est créé immédiatement
                avec un mot de passe temporaire transmis à l'utilisateur. */}
            <label className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors" data-testid="create-directly-toggle">
              <input
                type="checkbox"
                checked={createDirectly}
                onChange={(e) => setCreateDirectly(e.target.checked)}
                className="mt-0.5"
              />
              <div className="flex-1 text-xs">
                <p className="font-semibold text-amber-900">
                  {lang === "fr" ? "Créer le compte directement (sans invitation)" : "Create account directly (no invitation)"}
                </p>
                <p className="text-amber-800 mt-0.5">
                  {lang === "fr"
                    ? "Compte créé immédiatement avec un mot de passe temporaire envoyé par email. L'utilisateur n'a pas besoin de cliquer un lien d'invitation."
                    : "Account created instantly with a temporary password emailed to the user. No invitation link needed."}
                </p>
              </div>
            </label>
            <button
              type="submit" disabled={inviteLoading || !inviteEmail || (createDirectly && !inviteName.trim())}
              className="w-full h-11 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
              data-testid="send-invite-btn"
            >
              {inviteLoading ? <SpinnerGap size={18} className="animate-spin" /> : <Envelope size={18} />}
              {createDirectly
                ? (lang === "fr" ? "Créer le compte directement" : "Create account directly")
                : (lang === "fr" ? "Envoyer l'invitation" : "Send invitation")}
            </button>
          </form>
          {inviteMsg && (
            <div
              className={`text-sm mt-3 font-medium whitespace-pre-wrap break-all p-3 rounded-lg ${
                inviteMsg.includes("Erreur") || inviteMsg.includes("Limite") || inviteMsg.includes("attente")
                  ? "text-destructive bg-destructive/5 border border-destructive/20"
                  : inviteMsg.includes("⚠️")
                    ? "text-amber-700 bg-amber-50 border border-amber-200"
                    : "text-teal-700 bg-teal-50 border border-teal-200"
              }`}
              data-testid="invite-feedback-msg"
            >
              {inviteMsg}
            </div>
          )}

          {/* Pending Invites */}
          {invites.length > 0 && (
            <div className="mt-5 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-2">{t("pending_invitations")} ({invites.length})</p>
              <div className="space-y-2">
                {invites.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between p-2.5 bg-secondary/50 rounded-lg" data-testid={`invite-${inv.id}`}>
                    <div className="flex items-center gap-2">
                      <Envelope size={14} className="text-muted-foreground" />
                      <span className="text-sm">{inv.email}</span>
                      <span className="text-xs bg-secondary px-2 py-0.5 rounded-full">{getCanonicalLabel(inv.role, roleLabelsFor(user, lang), false, lang)}</span>
                      {/* Vincent 2026-06-03 (Eric — item 10) — Statut "Invité" sur chaque invitation pending. */}
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`invite-status-${inv.id}`}>
                        {lang === "fr" ? "Invité" : "Invited"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => copyInviteLink(inv.id)}
                        className="px-2 py-1 hover:bg-primary/10 rounded text-xs text-primary font-medium flex items-center gap-1"
                        data-testid={`copy-invite-link-${inv.id}`}
                        title={lang === "fr" ? "Copier le lien d'invitation" : "Copy invitation link"}
                      >
                        📋 {lang === "fr" ? "Copier le lien" : "Copy link"}
                      </button>
                      <button onClick={() => cancelInvite(inv.id)} className="p-1 hover:bg-destructive/10 rounded" data-testid={`cancel-invite-${inv.id}`}>
                        <Trash size={14} className="text-destructive" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Members List */}
      <div className="bg-card border border-border rounded-xl p-5" data-testid="members-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Users size={18} className="text-primary" /> Membres ({members.length})
          </h3>
          <SortDropdown
            value={membersSortBy}
            onChange={setMembersSortBy}
            testId="members-sort"
          />
        </div>
        <div className="space-y-2">
          {sortedMembers.map((m) => {
            const RoleIcon = ROLE_ICONS[m.org_role] || UserCircle;
            const isEditing = editingMember === m.id;
            return (
              <div key={m.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg" data-testid={`member-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <RoleIcon size={16} className="text-primary" />
                  </div>
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      {m.name}
                      {/* Vincent 2026-06-03 (Eric) — Chip de rôle subtil INLINE
                          avec le nom (PAS à gauche du cartable, qui était le
                          doublon Vincent #5). Permet de voir d'un coup d'œil le
                          rôle sans tooltip. Tiré des libellés canoniques. */}
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold"
                        data-testid={`role-chip-${m.id}`}
                      >
                        {getCanonicalLabel(m.org_role, roleLabelsFor(user, lang), m.is_founder, lang)}
                      </span>
                      {/* Vincent 2026-06-03 (Eric — item 10) — Statut d'approbation visible :
                          - approved : "Accepté" vert
                          - pending_approval : "En attente" amber
                          - rejected : "Refusé" rouge */}
                      {m.account_status && m.account_status !== "approved" ? (
                        <span
                          className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                            m.account_status === "pending_approval" ? "bg-amber-100 text-amber-700" :
                            m.account_status === "rejected" ? "bg-red-100 text-red-700" :
                            "bg-zinc-100 text-zinc-700"
                          }`}
                          data-testid={`status-${m.id}`}
                        >
                          {m.account_status === "pending_approval" ? (lang === "fr" ? "En attente" : "Pending") :
                           m.account_status === "rejected" ? (lang === "fr" ? "Refusé" : "Rejected") :
                           m.account_status}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700" data-testid={`status-${m.id}`}>
                          {lang === "fr" ? "Accepté" : "Accepted"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{m.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <div className="flex items-center gap-1">
                      <select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                        className="h-8 px-2 text-xs border border-border rounded-lg bg-background"
                        data-testid={`edit-role-select-${m.id}`}
                      >
                        {/* Vincent 2026-06-01 — Liste canonique unique des rôles
                            (Gestion des rôles). Plus de president/ceo/vp séparés. */}
                        {getCanonicalRoles(user, lang).map(r => (
                          <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => updateMemberRole(m.id, editRole)}
                        className="h-8 px-3 bg-primary text-primary-foreground text-xs rounded-lg hover:opacity-90"
                        data-testid={`save-role-${m.id}`}
                      >
                        OK
                      </button>
                      <button
                        onClick={() => setEditingMember(null)}
                        className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                      >
                        Annuler
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Vincent 2026-06-01 — Étiquette de rôle À GAUCHE du cartable retirée.
                          Le rôle s'affiche désormais via tooltip du cartable et dans la modal. */}
                      {isAdmin && (
                        <button
                          onClick={() => updateMemberTitle(m.id, m.job_title)}
                          className="p-1.5 hover:bg-primary/10 rounded"
                          title={m.job_title ? `Rôle actuel : ${m.job_title} — cliquer pour modifier` : `Rôle : ${getCanonicalLabel(m.org_role, roleLabelsFor(user, lang), false, lang)} — cliquer pour personnaliser`}
                          data-testid={`edit-title-${m.id}`}
                        >
                          💼
                        </button>
                      )}
                      {isAdmin && m.id !== user?.id && (
                        <>
                          {/* Vincent #288 (2026-06-11) — Réinitialiser l'accès :
                              envoie un courriel avec lien de réinitialisation
                              de mot de passe (1h, usage unique). */}
                          <button
                            onClick={async () => {
                              const ok = await confirmDialog(
                                `Envoyer un courriel de réinitialisation d'accès à ${m.email} ? Il recevra un lien (valide 1 heure) pour créer un nouveau mot de passe.`,
                                { confirmLabel: "Envoyer le courriel" },
                              );
                              if (!ok) return;
                              try {
                                const { data } = await axios.post(`${API}/api/organizations/members/${m.id}/send-reset`, {}, { withCredentials: true });
                                if (data.email_sent) {
                                  toast.success(`Courriel de réinitialisation envoyé à ${data.email}`);
                                } else {
                                  toast.warning(`Lien créé mais le courriel n'a pas pu être envoyé (vérifiez la configuration Resend). Email cible : ${data.email}`);
                                }
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Échec de l'envoi du lien de réinitialisation");
                              }
                            }}
                            className="p-1.5 hover:bg-amber-50 rounded"
                            title="Réinitialiser l'accès (envoie un courriel avec lien de nouveau mot de passe)"
                            data-testid={`reset-access-${m.id}`}
                          >
                            🔑
                          </button>
                          <button
                            onClick={() => { setEditingMember(m.id); setEditRole(m.org_role || "employee"); }}
                            className="p-1.5 hover:bg-primary/10 rounded"
                            title={t("change_role")}
                            data-testid={`edit-role-${m.id}`}
                          >
                            <PencilSimple size={14} className="text-primary" />
                          </button>
                          {m.org_role !== "ceo" && m.org_role !== "president" && (
                            <button onClick={() => removeMember(m.id)} className="p-1.5 hover:bg-destructive/10 rounded" data-testid={`remove-member-${m.id}`}>
                              <Trash size={14} className="text-destructive" />
                            </button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Vincent 2026-06-01 — Modal "cartable" : les options sont EXCLUSIVEMENT
          les 8 rôles canoniques de "Gestion des rôles" (avec libellés perso). */}
      {titleEditTarget && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={() => setTitleEditTarget(null)} data-testid="job-title-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">💼 {lang === "en" ? "Choose a role" : "Choisir un rôle"}</h3>
            <p className="text-xs text-zinc-500 mb-3">{lang === "en" ? "Select from the canonical role list. Selecting the blank entry clears the custom label." : "Sélectionnez dans la liste canonique. Le choix vide efface l'étiquette personnalisée."}</p>
            <select
              defaultValue={titleEditTarget.currentTitle}
              onChange={(e) => saveMemberTitle(titleEditTarget.memberId, e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
              data-testid="job-title-select"
              autoFocus
            >
              <option value="">— Aucun —</option>
              {getCanonicalRoles(user, lang).map((r) => (
                <option key={r.id} value={r.label}>{r.label}</option>
              ))}
            </select>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setTitleEditTarget(null)} className="px-3 py-1.5 text-sm bg-zinc-100 hover:bg-zinc-200 rounded" data-testid="job-title-cancel">{lang === "en" ? "Cancel" : "Annuler"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
